// src/sections/Hero.tsx
import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaSearch, FaGooglePlay, FaApple } from 'react-icons/fa';
import backgroundImage from '../assets/bg.jpg';
import { useTranslation } from 'react-i18next';
import { LoginPromptModal } from './SearchModals';
import { useNavigate, useLocation } from 'react-router-dom';

const HeroSection: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation(); // Hook to access the current location and its state
    const searchInputRef = useRef<HTMLInputElement>(null); // Ref for the search input

    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [phoneNumber, setPhoneNumber] = useState('');
    const [searchResult, setSearchResult] = useState<{ name: string; address: string; dob: string } | null>(null);
    const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
    const [isResultModalOpen, setIsResultModalOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    // Initial check on component mount.
    useEffect(() => {
        const storedUser = localStorage.getItem('userName');
        if (storedUser) {
            setIsLoggedIn(true);
        }
    }, []);

    // New useEffect hook to handle auto-focus
    useEffect(() => {
        // Check if the navigation state includes a 'focusSearch' flag
        if (location.state?.focusSearch) {
            // If the flag is present, focus the search input
            searchInputRef.current?.focus();
            // Optional: Clean up the state to prevent re-focusing on every re-render
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [location.state, navigate, location.pathname]);

    // Function to handle a simulated login
    const handleLogin = () => {
        // Simulate a successful login by saving a user name.
        localStorage.setItem('userName', 'Priya Rajput');
        setIsLoggedIn(true);
        setIsLoginModalOpen(false); // Close the modal after "login"
    };

    // Function to handle a simulated logout
    const handleLogout = () => {
        localStorage.removeItem('userName');
        setIsLoggedIn(false);
    };

    const handleSearch = async () => {
        if (!isLoggedIn) {
            setIsLoginModalOpen(true);
            return;
        }
        if (phoneNumber.length === 0) {
            return;
        }
        setLoading(true);
        try {
            const response = await fetch(`http://localhost:8383/api/search?phoneNumber=${phoneNumber}`, {
                method: 'GET',
                credentials: 'include'
            });

            if (response.status === 401) {
                setIsLoggedIn(false);
                setIsLoginModalOpen(true);
                return;
            }

            const result = await response.json();
            navigate('/search-result', {
                state: {
                    phoneNumber: phoneNumber,
                    result: result
                }
            });
        } catch (error) {
            console.error('Search fetch error:', error);
            navigate('/search-result', {
                state: {
                    phoneNumber: phoneNumber,
                    result: null
                }
            });
        } finally {
            setLoading(false);
        }
    };
    
    const handleCloseModals = () => {
        setIsLoginModalOpen(false);
        setIsResultModalOpen(false);
    };

    return (
        <section
            id="hero"
            className="relative w-full min-h-[650px] flex items-center justify-center overflow-hidden text-white"
        >
            {/* Background Image */}
            <img
                src={backgroundImage}
                alt={t('hero_background_alt')}
                className="absolute inset-0 w-full h-[900px] object-cover z-0"
            />

            {/* Overlay to ensure text readability */}
            <motion.div
                className="absolute inset-0 bg-gradient-to-br from-[#1A237E] via-[#004D40] to-[#1A237E] opacity-75 z-10"
                initial={{ opacity: 0 }}
                animate={{ opacity: 0.75 }}
                transition={{ duration: 1.5 }}
            ></motion.div>

            <div className="relative z-20 container mx-auto px-6 py-16 md:py-0 flex flex-col md:flex-row items-center justify-between gap-12">
                {/* Left: Content Area */}
                <div className="w-full md:w-1/2 text-center md:text-left">
                    <motion.h1
                        className="text-4xl md:text-5xl lg:text-6xl font-extrabold leading-tight text-white drop-shadow-lg"
                        initial={{ opacity: 0, y: 50 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                    >
                        {t('hero_heading')}
                    </motion.h1>

                    <motion.p
                        className="text-lg md:text-xl text-gray-200 mt-4 max-w-xl mx-auto md:mx-0"
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.4 }}
                    >
                        {t('hero_description')}
                    </motion.p>

                    {/* Search Bar - IMPROVED */}
                    <motion.div
                        className="flex items-center bg-white rounded-full p-1 pr-1 shadow-xl max-w-sm mx-auto md:mx-0 mt-8 transform hover:scale-[1.01] transition-transform duration-200 sm:max-w-md"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.6 }}
                    >
                        <select className="flex-shrink-0 px-2 py-2 text-gray-700 bg-gray-100 rounded-full text-sm sm:px-3 sm:text-base focus:outline-none focus:ring-2 focus:ring-lime-400">
                            <option value="+91">{t('country_code_india')}</option>
                            <option value="+1">{t('country_code_usa')}</option>
                            <option value="+44">{t('country_code_uk')}</option>
                        </select>
                        <input
                            ref={searchInputRef} // Attach the ref here
                            type="tel"
                            placeholder={t('search_phone_placeholder')}
                            className="flex-grow px-3 py-2 text-gray-800 focus:outline-none rounded-l-full text-sm sm:text-base"
                            value={phoneNumber}
                            onChange={(e) => setPhoneNumber(e.target.value)}
                            onKeyPress={(e) => {
                                if (e.key === 'Enter') handleSearch();
                            }}
                            disabled={loading}
                        />
                        <button
                            onClick={handleSearch}
                            className={`bg-[#AEEA00] text-[#1A237E] p-2 rounded-full flex items-center justify-center hover:bg-lime-600 transition-colors duration-300 transform hover:scale-110 sm:p-3 ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                            disabled={loading}
                        >
                            <FaSearch className="text-lg sm:text-xl" />
                        </button>
                    </motion.div>

                    {/* CTA Buttons */}
                    <motion.div
                        className="flex flex-col sm:flex-row justify-center md:justify-start items-center gap-4 mt-10"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.8 }}
                    >
                        <motion.a
                            href="#"
                            className="bg-[#AEEA00] text-[#1A237E] font-bold py-3 px-8 rounded-full shadow-lg hover:bg-gray-100 transition-colors duration-300 whitespace-nowrap border-2 border-transparent hover:border-[#AEEA00]"
                            whileHover={{ scale: 1.05, boxShadow: "0 8px 20px rgba(0,0,0,0.2)" }}
                            whileTap={{ scale: 0.95 }}
                        >
                            {t('start_free_trial_button')}
                        </motion.a>
                        <motion.a
                            href="https://play.google.com/store/apps/details?id=com.COC"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center bg-black text-white rounded-lg px-4 py-2 hover:bg-gray-800 transition-all duration-300 shadow-lg"
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                        >
                            <FaGooglePlay className="text-2xl mr-3 text-green-400" />
                            <div className="text-left text-sm leading-tight">
                                <div className="text-xs">{t('get_it_on_label')}</div>
                                <div className="font-semibold text-base">{t('google_play_label')}</div>
                            </div>
                        </motion.a>
                        <motion.a
                            href="https://apps.apple.com/us/app/COC-spam-blocker/id525881958"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center bg-black text-white rounded-lg px-4 py-2 hover:bg-gray-800 transition-all duration-300 shadow-lg"
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                        >
                            <FaApple className="text-2xl mr-3" />
                            <div className="text-left text-sm leading-tight">
                                <div className="text-xs">{t('download_on_label')}</div>
                                <div className="font-semibold text-base">{t('app_store_label')}</div>
                            </div>
                        </motion.a>
                    </motion.div>
                </div>
            </div>
            
            <AnimatePresence>
                {isLoginModalOpen && (
                    <LoginPromptModal
                        key="login-modal" 
                        isOpen={isLoginModalOpen}
                        onClose={handleCloseModals}
                        // Pass the new login function to the modal.
                        onSignInClick={handleLogin}
                    />
                )}
            </AnimatePresence>
        </section>
    );
};

export default HeroSection;